import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "@volca/ui/theme";
import { AuthProvider } from "@/core/auth";
import { BusinessProvider } from "@/core/business";
import { EnvProvider } from "@/core/env";
import { FeatureFlagProvider } from "@/core/feature-flags";
import { ImpersonationProvider } from "@/core/impersonation";
import { getPostHogConfig } from "@/core/posthog/config";
import SentryProvider from "@/core/sentry/sentry-provider";
import { ViewPreferencesProvider } from "@/core/view-preferences/view-preferences-context";
import AppContent from "./app-content";
import { BusinessSettingsSidebarProvider } from "./business-settings/context/business-settings-sidebar-context";

if (
  !import.meta.env.DEV &&
  import.meta.env.VITE_POSTHOG_PUBLIC_KEY &&
  import.meta.env.VITE_POSTHOG_PUBLIC_HOST
) {
  posthog.init(
    import.meta.env.VITE_POSTHOG_PUBLIC_KEY,
    getPostHogConfig(
      import.meta.env.VITE_API_URL,
      import.meta.env.VITE_POSTHOG_PUBLIC_HOST,
    ),
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on 401 errors
        if ((error as AxiosError)?.response?.status === 401) {
          return false;
        }
        // Default to 3 retries for other errors
        return failureCount < 3;
      },
    },
  },
});

const App = () => {
  const content = (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <NuqsAdapter>
          <AuthProvider>
            <ImpersonationProvider>
              <BusinessProvider>
                <FeatureFlagProvider>
                  <ViewPreferencesProvider>
                    {/* index.html sets the class before paint with the same key and default. */}
                    <ThemeProvider
                      defaultTheme="system"
                      storageKey="vite-ui-theme"
                    >
                      <BusinessSettingsSidebarProvider>
                        <AppContent />
                      </BusinessSettingsSidebarProvider>
                    </ThemeProvider>
                  </ViewPreferencesProvider>
                </FeatureFlagProvider>
              </BusinessProvider>
            </ImpersonationProvider>
          </AuthProvider>
        </NuqsAdapter>
      </BrowserRouter>
    </QueryClientProvider>
  );

  return (
    <EnvProvider>
      {import.meta.env.DEV ? (
        content
      ) : (
        <PostHogProvider client={posthog}>
          <SentryProvider>{content}</SentryProvider>
        </PostHogProvider>
      )}
    </EnvProvider>
  );
};

export default App;
